/**
 * dd/mm/yyyy helpers.
 *
 * The panel shows and accepts dates as dd/mm/yyyy. A native `<input type=date>`
 * renders in the browser's locale instead, which is why these exist: the text
 * the admin sees is ours, and the native control is used only for its picker.
 */

/** Today as dd/mm/yyyy. */
export function todayKey() {
  return toDayMonthYear(new Date());
}

/** A Date (or ISO string) as dd/mm/yyyy, read in UTC — these are calendar days. */
export function toDayMonthYear(value) {
  if (!value) return '';
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';

  return [
    String(parsed.getUTCDate()).padStart(2, '0'),
    String(parsed.getUTCMonth() + 1).padStart(2, '0'),
    parsed.getUTCFullYear(),
  ].join('/');
}

/**
 * Parses dd/mm/yyyy, returning null unless it is a real calendar day.
 *
 * The round-trip check is what rejects 31/02/2025: `Date.UTC` would silently
 * roll that forward to 3 March rather than failing.
 */
export function parseDayMonthYear(value) {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(value).trim());
  if (!match) return null;

  const [, day, month, year] = match.map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));

  const isReal =
    parsed.getUTCDate() === day &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCFullYear() === year;

  return isReal ? parsed : null;
}

/** Inserts the slashes as the field is typed, so the format is not a chore. */
export function maskDayMonthYear(value) {
  const digits = String(value).replace(/\D/g, '').slice(0, 8);
  const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)];
  return parts.filter(Boolean).join('/');
}

/** dd/mm/yyyy -> yyyy-mm-dd, the value a native date input holds. */
export function toIsoKey(value) {
  const parsed = parseDayMonthYear(value);
  return parsed ? parsed.toISOString().slice(0, 10) : '';
}
